import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityId, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { Integration } from '@sendhub/integrations/api-client-typescript-axios'
import { integrationsApi } from '../../services/integrations-api'
import { RootState } from '../root'

export const INTEGRATIONS_FEATURE_KEY = 'integrations'

export interface IntegrationsState extends EntityState<Readonly<Integration>> {
  loadingStatus: 'not loaded' | 'loading' | 'loaded' | 'error'
  error?: string | null
}

export const integrationsAdapter = createEntityAdapter<Readonly<Integration>>()

/**
 * Export an effect using createAsyncThunk from
 * the Redux Toolkit: https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(fetchIntegrations())
 * }, [dispatch]);
 * ```
 */
export const fetchIntegrations = createAsyncThunk('integrations/fetchStatus', async (_, thunkAPI) => {
  return integrationsApi.listIntegrations().then((r) => r.data)
})

export const initialIntegrationsState: IntegrationsState = integrationsAdapter.getInitialState({
  loadingStatus: 'not loaded',
  error: null,
})

export const integrationsSlice = createSlice({
  name: INTEGRATIONS_FEATURE_KEY,
  initialState: initialIntegrationsState,
  reducers: {
    add: integrationsAdapter.addOne,
    setMany: integrationsAdapter.setMany,
    remove: integrationsAdapter.removeOne,
    // ...
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIntegrations.pending, (state: IntegrationsState) => {
        state.loadingStatus = 'loading'
      })
      .addCase(fetchIntegrations.fulfilled, (state: IntegrationsState, action: PayloadAction<Integration[]>) => {
        integrationsAdapter.setAll(state, action.payload)
        state.loadingStatus = 'loaded'
      })
      .addCase(fetchIntegrations.rejected, (state: IntegrationsState, action) => {
        state.loadingStatus = 'error'
        state.error = action.error.message
      })
  },
})

/*
 * Export reducer for store configuration.
 */
export const integrationsReducer = integrationsSlice.reducer

/*
 * Export action creators to be dispatched. For use with the `useDispatch` hook.
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(integrationsActions.add({ id: 1 }))
 * }, [dispatch]);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#usedispatch
 */
export const integrationsActions = integrationsSlice.actions

/*
 * Export selectors to query state. For use with the `useSelector` hook.
 *
 * e.g.
 * ```
 * import { useSelector } from 'react-redux';
 *
 * // ...
 *
 * const entities = useSelector(selectAllIntegrations);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#useselector
 */
const { selectAll, selectEntities, selectById } = integrationsAdapter.getSelectors()

export const getIntegrationsState = (rootState: RootState): IntegrationsState => rootState[INTEGRATIONS_FEATURE_KEY]

export const selectIntegrationById = (id: EntityId) => (state: RootState) => selectById(getIntegrationsState(state), id)
export const selectAllIntegrations = createSelector(getIntegrationsState, selectAll)

export const selectIntegrationsEntities = createSelector(getIntegrationsState, selectEntities)
